import { Listing, Order, Product, User } from '@/api'
import { sendGTMEvent } from '@next/third-parties/google'
import { useCallback } from 'react'

type EventName =
  | 'begin_checkout'
  | 'add_to_cart'
  | 'purchase'
  | 'view_item'
  | 'view_home'
  | 'view_item_list'
  | 'tradein_form_submit'
  | 'tradein_complete'
  | 'tradein_item_color_select'
  | 'tradein_price_estimate'
  | 'add_to_favorites'

interface EventContext {
  product: Product | null
  currentUser: User | null
  currentOrder?: Order | null
  listing?: Listing | null
  tradeinId?: string | null
  listings?: Listing[] | null
  itemListId?: string | null
  itemListName?: string | null
  listingId?: string | null
}

type EventHandler = (context: EventContext) => any | null

export const useGTMTracking = () => {
  const eventHandlers: Record<EventName, EventHandler> = {
    view_item: ({ product, currentUser, listing }) => {
      if (!product) return null

      return {
        event: 'view_item',
        user_id: currentUser?.id || '',
        item_id: product?.uuid || '',
        item_name: product?.productMaster?.name || '',
        product_number: product?.styleColor || '',
        color: product?.color?.name || '',
        size: listing?.size?.name || '',
        condition: listing?.condition?.name || '',
        price: listing?.price || 0,
        ecommerce: {
          currency: 'JPY',
          value: listing?.price || 0,
          items: [
            {
              item_id: product?.uuid || '',
              item_name: product?.productMaster?.name || '',
              item_category: product?.styleColor || '',
              price: listing?.price || 0,
              quantity: 1,
              size: listing?.size?.name || '',
              condition: listing?.condition?.name || '',
              color: product?.color?.name || '',
            },
          ],
        },
      }
    },

    add_to_cart: ({ product, currentUser, listing }) => {
      if (!product || !listing) return null

      return {
        event: 'add_to_cart',
        user_id: currentUser?.id || '',
        item_id: product?.uuid || '',
        item_name: product?.productMaster?.name || '',
        product_number: product?.styleColor || '',
        color: product?.color?.name || '',
        size: listing?.size?.name || '',
        condition: listing?.condition?.name || '',
        price: listing?.price || 0,
        ecommerce: {
          currency: 'JPY',
          value: listing?.price || 0,
          items: [
            {
              item_id: product?.uuid || '',
              item_name: product?.productMaster?.name || '',
              item_category: product?.styleColor || '',
              price: listing?.price || 0,
              quantity: 1,
              size: listing?.size?.name || '',
              condition: listing?.condition?.name || '',
              color: product?.color?.name || '',
            },
          ],
        },
      }
    },

    begin_checkout: ({ currentUser, currentOrder }) => {
      if (!currentUser || !currentOrder?.items?.length) return null

      return {
        event: 'begin_checkout',
        user_id: currentUser.id || '',
        order_id: currentOrder.uuid || '',
        transaction_id: currentOrder.uuid || '',
        ecommerce: {
          transaction_id: currentOrder.uuid || '',
          currency: 'JPY',
          value: currentOrder.totalPrice || 0,
          items: currentOrder.items.map((item) => ({
            item_id: item?.listing?.product?.uuid || '',
            item_name: item?.listing?.product?.productMaster?.name || '',
            item_category:
              item.listing.product?.styleColor || '',
            price: item?.listing?.price || 0,
            quantity: 1,
            size: item?.listing?.size?.name || '',
            condition: item?.listing?.condition?.name || '',
            color: item?.listing?.product?.color?.name || '',
          })),
        },
      }
    },

    purchase: ({ currentUser, currentOrder }) => {
      if (!currentOrder?.orderNumber || !currentOrder.items?.length) return null

      // 注文の UUID は注文を操作する鍵になるため、外部に送らず注文番号を使う
      const transactionId = String(currentOrder.orderNumber)

      return {
        event: 'purchase',
        user_id: currentUser?.id || '',
        order_id: transactionId,
        transaction_id: transactionId,
        ecommerce: {
          transaction_id: transactionId,
          currency: 'JPY',
          value: Math.round((currentOrder.itemTotalPrice || 0) / 1.1),
          shipping: currentOrder.shippingTotalPrice || 0,
          items: currentOrder.items.map((item) => ({
            item_id: item?.listing?.product?.uuid || '',
            item_name: item?.listing?.product?.productMaster?.name || '',
            item_category:
              item.listing.product?.styleColor || '',
            price: item?.listing?.price || 0,
            quantity: 1,
            size: item?.listing?.size?.name || '',
            condition: item?.listing?.condition?.name || '',
            color: item?.listing?.product?.color?.name || '',
          })),
        },
      }
    },

    tradein_form_submit: ({ product, currentUser }) => {
      if (!currentUser || !product) return null

      return {
        event: 'tradein_form_submit',
        user_id: currentUser?.id || '',
        item_id: product?.uuid || '',
        item_name: product?.productMaster?.name || '',
        product_number: product?.styleColor || '',
        color: product?.color?.name || '',
      }
    },

    tradein_complete: ({ product, currentUser, tradeinId }) => {
      if (!currentUser || !product) return null

      return {
        event: 'tradein_complete',
        user_id: currentUser?.id || '',
        tradein_id: tradeinId || '',
        item_id: product?.uuid || '',
        item_name: product?.productMaster?.name || '',
        product_number: product?.styleColor || '',
        color: product?.color?.name || '',
      }
    },

    tradein_item_color_select: ({ product, currentUser }) => {
      if (!currentUser || !product) return null

      return {
        event: 'tradein_item_color_select',
        user_id: currentUser?.id || '',
        item_id: product?.uuid || '',
        item_name: product?.productMaster?.name || '',
        product_number: product?.styleColor || '',
        color: product?.color?.name || '',
      }
    },

    tradein_price_estimate: ({ product, currentUser }) => {
      if (!currentUser || !product) return null

      return {
        event: 'tradein_price_estimate',
        user_id: currentUser?.id || '',
        item_id: product?.uuid || '',
        item_name: product?.productMaster?.name || '',
        product_number: product?.styleColor || '',
        color: product?.color?.name || '',
      }
    },

    view_home: ({ currentUser }) => {
      return {
        event: 'view_home',
        user_id: currentUser?.id || '',
      }
    },

    view_item_list: ({ currentUser, listings, itemListId, itemListName }) => {
      if (!listings || listings.length === 0) return null

      return {
        event: 'view_item_list',
        user_id: currentUser?.id || '',
        ecommerce: {
          item_list_id: itemListId || 'all_listings',
          item_list_name: itemListName || '全ての商品',
          items: listings.map((listing, index) => ({
            item_id: listing.product?.uuid || '',
            item_name: listing.product?.productMaster?.name || '',
            item_category: listing.product?.styleColor || '',
            price: listing.price || 0,
            quantity: 1,
            index,
            color: listing.product?.color?.name || '',
            size: listing.size?.name || '',
            condition: listing.condition?.name || '',
          })),
        },
      }
    },

    add_to_favorites: ({ product, currentUser, listing }) => {
      if (!currentUser || !product || !listing) return null

      return {
        event: 'add_to_favorites',
        user_id: currentUser?.id || '',
        item_id: product?.uuid || '',
        item_name: product?.productMaster?.name || '',
        product_number: product?.styleColor || '',
        color: product?.color?.name || '',
        size: listing?.size?.name || '',
        condition: listing?.condition?.name || '',
        price: listing?.price || 0,
      }
    },
  }

  const trackEvent = useCallback(
    (
      eventName: EventName,
      product: Product | null,
      currentUser: User | null,
      currentOrder?: Order | null,
      listing?: Listing | null,
      tradeinId?: string | null,
      listings?: Listing[] | null,
      itemListId?: string | null,
      itemListName?: string | null,
      listingId?: string | null
    ) => {
      const handler = eventHandlers[eventName]

      if (!handler) {
        console.warn(`Unknown event type: ${eventName}`)
        return
      }

      const eventData = handler({
        product,
        currentUser,
        currentOrder,
        listing,
        tradeinId,
        listings,
        itemListId,
        itemListName,
        listingId,
      })

      if (eventData) {
        sendGTMEvent(eventData)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return { trackEvent }
}
